// src/components/layout/Header.tsx
import React, { useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Menu,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  useMediaQuery,
  Divider,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";

const palette = {
  bg: "#EEF5FF",
  panel: "#B4D4FF",
  border: "#86B6F6",
  accent: "#176B87",
  ink: "#0B2230",
  white: "#FFFFFF",
  inkMuted: "rgba(11,34,48,0.72)",
  accentSoft: "rgba(23,107,135,0.12)",
};

type NavLeaf = { label: string; href: string };
type NavGroup = { label: string; subItems: NavLeaf[] };
type NavItem = NavLeaf | NavGroup;

const isGroup = (item: NavItem): item is NavGroup => "subItems" in item;
const isLeaf = (item: NavItem): item is NavLeaf => "href" in item;

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const navItems: NavItem[] = useMemo(
    () => [
      { label: "Home", href: "/" },
      { label: "Dashboard", href: "/stats" },
      {
        label: "Team Performance",
        subItems: [
          { label: "Overall Performance", href: "/performance" },
          { label: "Team Comparison Analysis", href: "/comparison" },
        ],
      },
      { label: "Table View", href: "/table" },
      { label: "World Cup", href: "/world" },
    ],
    []
  );

  // Desktop menu (hover dropdown)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);

  // Mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerOpenIndex, setDrawerOpenIndex] = useState<number | null>(null);

  const currentPath = location.pathname;

  const isRouteActive = (href?: string) => {
    if (!href) return false;
    if (href === "/") return currentPath === "/";
    return currentPath === href || currentPath.startsWith(href + "/");
  };

  const groupHasActiveChild = (subItems: NavLeaf[]) => subItems.some((s) => isRouteActive(s.href));

  const closeAllUi = () => {
    setAnchorEl(null);
    setOpenMenuIndex(null);
    setDrawerOpen(false);
    setDrawerOpenIndex(null);
  };

  const handleNavigate = (href: string) => {
    navigate(href);
    closeAllUi();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setAnchorEl(event.currentTarget);
    setOpenMenuIndex(index);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenMenuIndex(null);
  };

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
    if (drawerOpen) setDrawerOpenIndex(null);
  };

  const toggleDrawerGroup = (index: number) => {
    setDrawerOpenIndex((prev) => (prev === index ? null : index));
  };

  const desktopBtnSx = (active: boolean) => ({
    fontSize: "1.05rem",
    fontWeight: 900,
    color: active ? palette.ink : palette.accent,
    textTransform: "none" as const,
    borderRadius: 2,
    px: 1.6,
    py: 0.9,
    backgroundColor: active ? palette.panel : "transparent",
    border: active ? `1px solid ${palette.border}` : "1px solid transparent",
    boxShadow: active ? "0 10px 24px rgba(23,107,135,0.12)" : "none",
    transition: "transform 0.08s ease, background-color 0.18s ease, box-shadow 0.18s ease",
    "&:hover": {
      backgroundColor: active ? palette.panel : palette.accentSoft,
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0px)",
    },
  });

  const drawerItemSx = (active: boolean) => ({
    borderRadius: 2,
    mx: 1,
    my: 0.4,
    backgroundColor: active ? palette.panel : "transparent",
    border: active ? `1px solid ${palette.border}` : "1px solid transparent",
    "&:hover": { backgroundColor: active ? palette.panel : palette.accentSoft },
  });

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: palette.bg,
        color: palette.ink,
        boxShadow: "none",
        borderBottom: `1px solid ${palette.border}`,
        zIndex: 1300,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: isMobile ? "space-between" : "center",
          gap: 2,
          minHeight: { xs: 64, md: 72 },
          px: { xs: 1.5, sm: 2.5 },
        }}
      >
        {isMobile ? (
          <>
            {/* LEFT: burger */}
            <IconButton
              edge="start"
              color="inherit"
              aria-label="Open navigation menu"
              onClick={toggleDrawer}
              sx={{
                borderRadius: 2,
                border: `1px solid ${palette.border}`,
                backgroundColor: palette.white,
                boxShadow: "0 10px 24px rgba(23,107,135,0.10)",
                "&:hover": { backgroundColor: palette.accentSoft },
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* CENTER: Home */}
            <Button
              onClick={() => handleNavigate("/")}
              startIcon={<HomeRoundedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 950,
                color: isRouteActive("/") ? palette.ink : palette.accent,
                backgroundColor: isRouteActive("/") ? palette.panel : "transparent",
                border: isRouteActive("/") ? `1px solid ${palette.border}` : "1px solid transparent",
                borderRadius: 2,
                px: 1.6,
                py: 0.9,
                minWidth: 0,
                "&:hover": { backgroundColor: palette.accentSoft },
              }}
            >
              Home
            </Button>

            {/* RIGHT spacer */}
            <Box sx={{ width: 44 }} />

            <Drawer
              anchor="left"
              open={drawerOpen}
              onClose={toggleDrawer}
              PaperProps={{
                sx: {
                  width: { xs: 300, sm: 320 },
                  backgroundColor: palette.bg,
                  borderRight: `1px solid ${palette.border}`,
                },
              }}
            >
              {/* Drawer header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.6,
                  borderBottom: `1px solid ${palette.border}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      backgroundColor: palette.panel,
                      border: `1px solid ${palette.border}`,
                      display: "grid",
                      placeItems: "center",
                      boxShadow: "0 10px 24px rgba(23,107,135,0.10)",
                    }}
                  >
                    <HomeRoundedIcon sx={{ color: palette.accent }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 950, color: palette.ink, lineHeight: 1.1 }}>
                      Rugby Insights
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: palette.inkMuted }}>
                      Navigate pages
                    </Typography>
                  </Box>
                </Box>

                <IconButton
                  onClick={toggleDrawer}
                  aria-label="Close navigation"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${palette.border}`,
                    backgroundColor: palette.white,
                    "&:hover": { backgroundColor: palette.accentSoft },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>

              <List sx={{ py: 1 }}>
                {navItems.map((item, index) => {
                  if (isLeaf(item)) {
                    const active = isRouteActive(item.href);
                    return (
                      <ListItem disablePadding key={item.href}>
                        <ListItemButton onClick={() => handleNavigate(item.href)} sx={drawerItemSx(active)}>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontWeight: 900,
                              color: active ? palette.ink : palette.accent,
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  }

                  // group
                  const active = groupHasActiveChild(item.subItems);
                  const isOpen = drawerOpenIndex === index;

                  return (
                    <React.Fragment key={item.label}>
                      <ListItem disablePadding>
                        <ListItemButton onClick={() => toggleDrawerGroup(index)} sx={drawerItemSx(active)}>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              fontWeight: 900,
                              color: active ? palette.ink : palette.accent,
                            }}
                          />
                          {isOpen ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                      </ListItem>

                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding sx={{ pb: 0.6 }}>
                          {item.subItems.map((sub) => {
                            const subActive = isRouteActive(sub.href);
                            return (
                              <ListItemButton
                                key={sub.href}
                                sx={{ ...drawerItemSx(subActive), ml: 2, mr: 1, pl: 2.2 }}
                                onClick={() => handleNavigate(sub.href)}
                              >
                                <ListItemText
                                  primary={sub.label}
                                  primaryTypographyProps={{
                                    fontWeight: 850,
                                    color: subActive ? palette.ink : palette.accent,
                                    fontSize: 14,
                                  }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  );
                })}

                <Divider sx={{ my: 1.2, borderColor: "rgba(134,182,246,0.55)" }} />

                <ListItem disablePadding>
                  <ListItemButton onClick={() => handleNavigate("/")} sx={drawerItemSx(isRouteActive("/"))}>
                    <ListItemText
                      primary="Back to Home"
                      primaryTypographyProps={{
                        fontWeight: 950,
                        color: isRouteActive("/") ? palette.ink : palette.accent,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Drawer>
          </>
        ) : (
          // Desktop nav
          navItems.map((item, index) => {
            if (isGroup(item)) {
              const active = groupHasActiveChild(item.subItems);

              return (
                <Box
                  key={item.label}
                  onMouseEnter={(e) => handleMenuOpen(e, index)}
                  onMouseLeave={handleMenuClose}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Button sx={desktopBtnSx(active)} aria-haspopup="menu" aria-expanded={openMenuIndex === index}>
                    {item.label}
                  </Button>

                  <Menu
                    anchorEl={anchorEl}
                    open={openMenuIndex === index}
                    onClose={handleMenuClose}
                    MenuListProps={{ onMouseLeave: handleMenuClose }}
                    sx={{
                      mt: 1,
                      "& .MuiMenu-paper": {
                        backgroundColor: palette.bg,
                        boxShadow: "0 10px 24px rgba(23,107,135,0.16)",
                        borderRadius: 2,
                        border: `1px solid ${palette.border}`,
                        p: 0.6,
                        minWidth: 260,
                      },
                    }}
                  >
                    {item.subItems.map((sub) => {
                      const subActive = isRouteActive(sub.href);
                      return (
                        <Box
                          key={sub.href}
                          sx={{
                            m: 0.45,
                            px: 1.3,
                            py: 1.0,
                            borderRadius: 2,
                            backgroundColor: subActive ? palette.panel : palette.white,
                            border: subActive ? `1px solid ${palette.border}` : `1px solid rgba(134,182,246,0.55)`,
                            cursor: "pointer",
                            fontSize: "0.95rem",
                            fontWeight: 900,
                            color: subActive ? palette.ink : palette.accent,
                            transition: "transform 0.08s ease, background-color 0.18s ease",
                            "&:hover": {
                              backgroundColor: palette.accentSoft,
                              transform: "translateY(-1px)",
                            },
                          }}
                          onClick={() => {
                            handleNavigate(sub.href);
                            handleMenuClose();
                          }}
                        >
                          {sub.label}
                        </Box>
                      );
                    })}
                  </Menu>
                </Box>
              );
            }

            // leaf
            const active = isRouteActive(item.href);
            return (
              <Button
                key={item.href}
                onClick={() => handleNavigate(item.href)}
                sx={desktopBtnSx(active)}
              >
                {item.label}
              </Button>
            );
          })
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
